import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRecordContext, useGetList, useGetOne, useGetIdentity, useDataProvider, useNotify, useRefresh } from 'react-admin';
import { apiFetchResponse } from '../../lib/apiFetch';
import {
  AlertCircle, MessageSquareText, Loader2, Mail,
  FileText, ClipboardCheck, Send, PenLine, CreditCard,
  FileCheck, Truck, Flame, PackageCheck, MapPin,
  CalendarCheck, CircleCheckBig, ClipboardList,
  FileSpreadsheet, FileStack, Heart, Star, Package, type LucideIcon,
} from 'lucide-react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Stack, Checkbox, Typography, Box,
  Button as MuiButton,
  Select, MenuItem, FormControl, InputLabel, Radio, RadioGroup,
  FormControlLabel, FormLabel,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Funeral, CustomerEventType, ReviewRequest, ReviewRequestSendMethod, Aftercare } from '../../types';
import moment from 'moment';
import {
  getStepsForRegion,
  getPositionedSteps,
  positionSteps,
  ALL_STEPS,
  ProcessStep,
  PositionedStep,
  FeedEvent,
} from './processSteps';
import { useMessageDraft } from '../contexts/MessageDraftContext';
import { IssueDocumentDialog } from '../documents/IssueDocumentDialog';
import { SendAttestationDialog } from '../documents/SendAttestationDialog';
import { UrnOrderDialog } from './UrnOrderDialog';
import { PaperworkCompleteNotificationDialog } from '../../components/PaperworkCompleteNotificationDialog';
import CppBenefitsMailingModal from '../CppBenefitsMailingModal';
import { getToken } from '../../authentication/authProvider';
import { PartnerDocsPanel } from '../../components/dispatch/PartnerDocsPanel';
import { getDefaultAftercareMessage as buildDefaultAftercareMessage } from '../../aftercare/aftercareEmailCopy';

const MAX_CARD_ROWS = 13;
const CHARS_PER_ROW = 40;

/** Default days from now that a review request is scheduled for. */
const REVIEW_REQUEST_DELAY_DAYS = 14;

function countCardRows(text: string): number {
  if (!text) return 0;
  return text.split('\n').reduce((rows, line) => {
    return rows + Math.max(1, Math.ceil(line.length / CHARS_PER_ROW));
  }, 0);
}

// ─── Icon lookup ──────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, LucideIcon> = {
  FileText, ClipboardCheck, Send, PenLine, CreditCard,
  FileCheck, Truck, Flame, PackageCheck, MapPin,
  CalendarCheck, CircleCheckBig, ClipboardList,
  FileSpreadsheet, FileStack, Heart, Star, Mail, Package,
};

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

/** Shape returned by the backend `GET /cases/:id/state` read model (Phase 1). */
type CaseStateStep = {
  id: string;
  label: string;
  status: 'done' | 'actionable' | 'blocked' | 'not_applicable';
  completedAt: string | null;
  reason?: string;
};

// ─── Verified document type ──────────────────────────────────────────────────

interface VerifiedDocument {
  id: string;
  verification_id: string;
  document_type: string;
  status: string;
}

// ─── Validation summary types ─────────────────────────────────────────────────

interface FormValidation {
  missingRequiredCount: number;
}

interface ValidationSummary {
  decForm: FormValidation;
  benefitsForm: FormValidation;
}

// ─── Date formatting ──────────────────────────────────────────────────────────

function formatCompletedDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ProcessTimeline() {
  const record = useRecordContext<Funeral>();
  const { setDraftMessage } = useMessageDraft();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [validation, setValidation] = useState<ValidationSummary | null>(null);
  const [loadingMessage, setLoadingMessage] = useState(false);

  // ─── Attestation state ───────────────────────────────────────────────
  const [verifiedDocs, setVerifiedDocs] = useState<VerifiedDocument[]>([]);
  const [funeralDocs, setFuneralDocs] = useState<any[]>([]);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [sendAttestationOpen, setSendAttestationOpen] = useState(false);

  // ─── Prep sheet state ────────────────────────────────────────────────
  const [sendPrepSheetOpen, setSendPrepSheetOpen] = useState(false);
  const [sendingPrepSheet, setSendingPrepSheet] = useState(false);
  const [ps, setPs] = useState({
    to: '',
    deceased_name: '',
    sex: '',
    weight: '',
    sp3: true,
    coroner: false,
    sp3_number: '',
    causes_of_death: '',
    preparation: 'basic' as 'basic' | 'embalming',
    pacemaker: 'no' as 'yes' | 'no',
    shaving: 'no' as 'yes' | 'no',
    shaving_detail: '',
    dentures: 'no' as 'yes' | 'no',
    dentures_detail: '',
    clothing: 'sheet' as 'sheet' | 'pyjama',
    cosmetics_no: true,
    hairdressing_photo: false,
    container: 'cardboard' as 'cardboard' | 'oversized',
    hair: 'no' as 'yes' | 'no',
    hair_count: '',
    notes: '',
  });
  const updatePs = (patch: Partial<typeof ps>) => setPs((prev) => ({ ...prev, ...patch }));

  // ─── Review request state ─────────────────────────────────────────────
  const [reviewRequest, setReviewRequest] = useState<ReviewRequest | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewViewOpen, setReviewViewOpen] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewRecipientClientId, setReviewRecipientClientId] = useState<number | ''>('');
  const [reviewSendMethod, setReviewSendMethod] = useState<ReviewRequestSendMethod>('email');
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewDelayDays, setReviewDelayDays] = useState(REVIEW_REQUEST_DELAY_DAYS);
  const [clientOptions, setClientOptions] = useState<{ id: number; name: string; type: string }[]>([]);

  // ─── Aftercare state ──────────────────────────────────────────────────
  const [aftercare, setAftercare] = useState<Aftercare | null>(null);
  const [aftercareDialogOpen, setAftercareDialogOpen] = useState(false);
  const [aftercareViewOpen, setAftercareViewOpen] = useState(false);
  const [aftercareSaving, setAftercareSaving] = useState(false);
  const [aftercareRecipientClientId, setAftercareRecipientClientId] = useState<number | ''>('');
  const [aftercareMessage, setAftercareMessage] = useState('');
  const [aftercareDelayDays, setAftercareDelayDays] = useState(5);
  const [aftercareEnabled, setAftercareEnabled] = useState(true);

  // ─── Paperwork-complete confirmation email state ──────────────────────
  const [paperworkNotifyOpen, setPaperworkNotifyOpen] = useState(false);

  // ─── Thank you card state ────────────────────────────────────────────
  const [cppMailingOpen, setCppMailingOpen] = useState(false);

  const [thankYouOpen, setThankYouOpen] = useState(false);
  const [urnOrderOpen, setUrnOrderOpen] = useState(false);
  const [sendingThankYou, setSendingThankYou] = useState(false);
  const [tyRecipientName, setTyRecipientName] = useState('');
  const [tyPlaceName, setTyPlaceName] = useState('');
  const [tyStreet, setTyStreet] = useState('');
  const [tyCity, setTyCity] = useState('');
  const [tyProvince, setTyProvince] = useState('');
  const [tyPostalCode, setTyPostalCode] = useState('');
  const [tyFloorSection, setTyFloorSection] = useState('');
  const [tyLetterText, setTyLetterText] = useState('');

  // Fetch primary client for email pre-population
  const { data: primaryClient } = useGetOne(
    'clients',
    { id: record?.primary_client_id },
    { enabled: !!record?.primary_client_id },
  );

  // Fetch paying ("buyer") and authorized clients to CC on attestation emails
  const { data: payingClient } = useGetOne(
    'clients',
    { id: record?.paying_client_id },
    { enabled: !!record?.paying_client_id },
  );
  const { data: authorizedClient } = useGetOne(
    'clients',
    { id: record?.authorized_client_id },
    { enabled: !!record?.authorized_client_id },
  );

  // Fetch deceased client — used by the prep sheet pre-fill and the
  // Send-Benefits-to-Service-Canada mailing modal.
  const { data: deceasedClient } = useGetOne(
    'clients',
    { id: record?.deceased_client_id },
    { enabled: !!record?.deceased_client_id },
  );

  // Fetch crematorium partner for prep sheet default emails
  const { data: crematoriumPartner } = useGetOne(
    'partners',
    { id: record?.crematorium_partner_id },
    { enabled: !!record?.crematorium_partner_id && !!record?.has_visitation },
  );

  // Fetch funeral feed events (same pattern as FuneralTimeline)
  const { data: events, refetch: refetchEvents } = useGetList(
    'funeral_feed',
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'timestamp', order: 'DESC' },
      filter: { funeral_id: record?.id },
    },
    { enabled: !!record?.id },
  );

  // Fetch verified documents
  const fetchVerifiedDocs = useCallback(async () => {
    if (!record?.id) return;
    try {
      const token = await getToken();
      const res = await fetch(`${baseUrl}/documents/funeral/${record.id}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setVerifiedDocs(data.verified_documents || []);
        setFuneralDocs(data.funeral_documents || []);
      }
    } catch {
      /* ignore */
    }
  }, [record?.id]);

  useEffect(() => {
    void fetchVerifiedDocs();
  }, [fetchVerifiedDocs]);

  // Fetch review request for this funeral
  const fetchReviewRequest = useCallback(async () => {
    if (!record?.id) return;
    try {
      const response = await dataProvider.getList('review_requests', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'created_at', order: 'DESC' },
        filter: { funeral_id: record.id },
      });
      if (response.data && response.data.length > 0) {
        setReviewRequest(response.data[0] as ReviewRequest);
      }
    } catch {
      /* ignore */
    }
  }, [dataProvider, record?.id]);

  useEffect(() => {
    void fetchReviewRequest();
  }, [fetchReviewRequest]);

  // Fetch client options for review request recipient dropdown
  useEffect(() => {
    const fetchClients = async () => {
      if (!record) return;
      const clients: { id: number; name: string; type: string }[] = [];

      if (record.primary_client_id) {
        try {
          const response = await dataProvider.getOne('clients', { id: record.primary_client_id });
          const client = response.data;
          clients.push({
            id: Number(client.id),
            name: client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim(),
            type: 'Primary',
          });
        } catch { /* ignore */ }
      }

      if (record.paying_client_id && record.paying_client_id !== record.primary_client_id) {
        try {
          const response = await dataProvider.getOne('clients', { id: record.paying_client_id });
          const client = response.data;
          clients.push({
            id: Number(client.id),
            name: client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim(),
            type: 'Paying',
          });
        } catch { /* ignore */ }
      }

      if (record.authorized_client_id &&
          record.authorized_client_id !== record.primary_client_id &&
          record.authorized_client_id !== record.paying_client_id) {
        try {
          const response = await dataProvider.getOne('clients', { id: record.authorized_client_id });
          const client = response.data;
          clients.push({
            id: Number(client.id),
            name: client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim(),
            type: 'Authorized',
          });
        } catch { /* ignore */ }
      }

      setClientOptions(clients);
      if (clients.length > 0) {
        setReviewRecipientClientId(clients[0].id);
      }
    };
    void fetchClients();
  }, [dataProvider, record?.primary_client_id, record?.paying_client_id, record?.authorized_client_id]);

  // Find valid POD document
  const podDoc = useMemo(
    () => verifiedDocs.find((d) => d.document_type === 'POD' && d.status === 'valid'),
    [verifiedDocs],
  );

  // Fetch validation summary (same pattern as ValidationStatusIndicator)
  useEffect(() => {
    if (!record?.id) return;
    let cancelled = false;

    apiFetchResponse(`validation/summary/${record.id}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.success) {
          setValidation(data.data);
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => { /* ignore */ });

    return () => {
      cancelled = true;
    };
  }, [record?.id]);

  // Case-state read model (Phase 1): the backend is the single source of truth
  // for step completion (and derives SP3/payment from documents/payments, not the
  // stale funerals booleans). Falls back to local evaluation until it responds.
  const [caseState, setCaseState] = useState<CaseStateStep[] | null>(null);
  useEffect(() => {
    if (!record?.id) return;
    let cancelled = false;
    setCaseState(null);
    apiFetchResponse(`cases/${record.id}/state`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.success) setCaseState(data.data as CaseStateStep[]);
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => { /* ignore — fall back to local eval */ });
    return () => {
      cancelled = true;
    };
  }, [record?.id]);

  // Build positioned steps
  const steps = useMemo(() => {
    if (!record) return [];
    // Backend read model available → merge its statuses with the static
    // presentation metadata (icon/color/floating) and reuse the same positioning.
    if (caseState) {
      const metaById = new Map(ALL_STEPS.map((s) => [s.id, s]));
      const evaluated: PositionedStep[] = caseState
        .filter((s) => s.status !== 'not_applicable' && metaById.has(s.id))
        .map((s) => ({
          ...(metaById.get(s.id) as ProcessStep),
          completed: s.status === 'done',
          completedAt: s.completedAt ?? null,
        }));
      return positionSteps(evaluated);
    }
    // Fallback: local evaluation (pre-response / endpoint error).
    const regionSteps = getStepsForRegion(record.region, record);
    return getPositionedSteps(regionSteps, record, (events as FeedEvent[]) || []);
  }, [record, caseState, events]);

  // Find the first incomplete step (current step)
  const currentStepIdx = useMemo(
    () => steps.findIndex((s) => !s.completed),
    [steps],
  );

  // Get missing count for a step's validation form
  const getMissingCount = (step: PositionedStep): number => {
    if (!validation || !step.validationForm) return 0;
    if (step.validationForm === 'dec') return validation.decForm.missingRequiredCount;
    if (step.validationForm === 'benefits') return validation.benefitsForm.missingRequiredCount;
    return 0;
  };

  // Request missing info message from backend
  const handleRequestMissingInfo = async () => {
    if (!record?.id) return;
    setLoadingMessage(true);
    try {
      const locale = record.language || 'en';
      const res = await apiFetchResponse(
        `validation/missing-data-message/${record.id}?locale=${locale}`,
      );
      if (!res.ok) throw new Error('Failed to fetch message');
      const data = await res.json();
      if (data?.data?.message) {
        setDraftMessage(data.data.message);
      }
    } catch (err) {
      console.error('Failed to get missing data message:', err);
    } finally {
      setLoadingMessage(false);
    }
  };

  // ─── Review request handlers ──────────────────────────────────────────
  const getDefaultReviewMessage = useCallback((recipientId: number | '') => {
    if (!record || !recipientId) return '';
    const reviewLink = record.region === 'ontario'
      ? (process.env.REACT_APP_REVIEW_LINK_ONTARIO || 'https://g.page/r/CaBtI8l6SQNFEBM/review')
      : (process.env.REACT_APP_REVIEW_LINK_QUEBEC || 'https://g.page/r/CWIjv-Au1sTLEBM/review');
    const selectedClient = clientOptions.find((c) => c.id === recipientId);
    const firstName = selectedClient?.name?.split(' ')[0] || '';
    const isFrench = record.language === 'fr';

    if (isFrench) {
      return `Bonjour${firstName ? ` ${firstName}` : ''}, c'est Bram de Cleo. Je voulais simplement prendre de vos nouvelles et vous remercier encore une fois de nous avoir fait confiance dans un moment aussi difficile.\n\nSi jamais vous vous sentez à l'aise de partager votre expérience, cela aide vraiment d'autres familles à nous trouver : ${reviewLink}\n\nN'hésitez pas à me répondre si vous avez besoin de quoi que ce soit.`;
    }
    return `Hi${firstName ? ` ${firstName}` : ''}, it's Bram from Cleo. I'm just checking in to see how you're doing and to thank you again for trusting us during such a difficult time.\n\nIf you ever feel comfortable sharing your experience, it really helps other families find us: ${reviewLink}\n\nYou can always reply here if you need anything.`;
  }, [record, clientOptions]);

  const openReviewDialog = (existing?: ReviewRequest | null) => {
    if (existing) {
      setReviewRecipientClientId(existing.recipient_client_id);
      setReviewSendMethod(existing.send_method);
      setReviewMessage(existing.message);
      // Calculate remaining days from scheduled_date
      const remaining = Math.max(1, Math.ceil(moment(existing.scheduled_date).diff(moment(), 'days', true)));
      setReviewDelayDays(remaining);
    } else {
      const defaultRecipient = clientOptions.length > 0 ? clientOptions[0].id : '';
      setReviewRecipientClientId(defaultRecipient);
      setReviewSendMethod('email');
      setReviewMessage(getDefaultReviewMessage(defaultRecipient));
      setReviewDelayDays(REVIEW_REQUEST_DELAY_DAYS);
    }
    setReviewDialogOpen(true);
  };

  const handleSaveReviewRequest = async () => {
    if (!record?.id || !reviewRecipientClientId) return;
    setReviewSaving(true);
    try {
      const scheduledDate = moment().add(reviewDelayDays, 'days').toISOString();
      const data = {
        funeral_id: Number(record.id),
        enabled: true,
        send_method: reviewSendMethod,
        recipient_client_id: reviewRecipientClientId,
        message: reviewMessage,
        scheduled_date: scheduledDate,
        created_by_user_id: identity?.id ? Number(identity.id) : null,
        created_by_user_name: identity?.fullName || 'System',
      };

      if (reviewRequest?.id) {
        await dataProvider.update('review_requests', {
          id: reviewRequest.id,
          data: { send_method: reviewSendMethod, recipient_client_id: reviewRecipientClientId, message: reviewMessage, scheduled_date: scheduledDate },
          previousData: reviewRequest,
        });
        notify('Review request updated', { type: 'success' });
      } else {
        await dataProvider.create('review_requests', { data });
        notify(`Review request scheduled for ${reviewDelayDays} days from now`, { type: 'success' });
      }
      setReviewDialogOpen(false);
      void fetchReviewRequest();
    } catch (e: any) {
      notify(e.message || 'Failed to save review request', { type: 'error' });
    } finally {
      setReviewSaving(false);
    }
  };

  const handleSendReviewNow = async () => {
    if (!record?.id || !reviewRecipientClientId) return;
    setReviewSaving(true);
    try {
      // Create or update the review request first, then send immediately
      const data = {
        funeral_id: Number(record.id),
        enabled: true,
        send_method: reviewSendMethod,
        recipient_client_id: reviewRecipientClientId,
        message: reviewMessage,
        scheduled_date: new Date().toISOString(),
        created_by_user_id: identity?.id ? Number(identity.id) : null,
        created_by_user_name: identity?.fullName || 'System',
      };

      let requestId = reviewRequest?.id;
      if (requestId) {
        await dataProvider.update('review_requests', {
          id: requestId,
          data: { send_method: reviewSendMethod, recipient_client_id: reviewRecipientClientId, message: reviewMessage, scheduled_date: data.scheduled_date },
          previousData: reviewRequest,
        });
      } else {
        const created = await dataProvider.create('review_requests', { data });
        requestId = created.data.id;
      }

      // Send immediately
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_PAPERMAN_API_URL}review-requests/${requestId}/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        },
      );
      const result = await response.json();

      if (response.ok && result.status === 'success') {
        notify('Review request sent', { type: 'success' });
      } else {
        notify(result.message || 'Failed to send review request', { type: 'error' });
      }

      setReviewDialogOpen(false);
      void fetchReviewRequest();
    } catch (e: any) {
      notify(e.message || 'Failed to send review request', { type: 'error' });
    } finally {
      setReviewSaving(false);
    }
  };

  const getSendMethodLabel = (method: string) => {
    switch (method) {
      case 'sms': return 'SMS';
      case 'email': return 'Email';
      case 'both': return 'SMS & Email';
      default: return method;
    }
  };

  // ─── Aftercare handlers ───────────────────────────────────────────────
  const fetchAftercare = useCallback(async () => {
    if (!record?.id) return;
    try {
      const response = await dataProvider.getList('aftercare', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'DESC' },
        filter: { funeral_id: record.id },
      });
      if (response.data && response.data.length > 0) {
        setAftercare(response.data[0] as Aftercare);
      } else {
        setAftercare(null);
      }
    } catch {
      /* ignore */
    }
  }, [dataProvider, record?.id]);

  useEffect(() => {
    void fetchAftercare();
  }, [fetchAftercare]);

  const getDefaultAftercareMessage = useCallback((recipientId: number | '') => {
    if (!record || !recipientId) return '';
    const selectedClient = clientOptions.find((c) => c.id === recipientId);
    const firstName = selectedClient?.name?.split(' ')[0] || '';
    return buildDefaultAftercareMessage(record.language, firstName);
  }, [record, clientOptions]);

  const openAftercareDialog = (existing?: Aftercare | null) => {
    const defaultRecipient = clientOptions.length > 0 ? clientOptions[0].id : '';
    if (existing) {
      setAftercareRecipientClientId(existing.email_recipient_client_id ?? defaultRecipient);
      setAftercareMessage(existing.email_message ?? getDefaultAftercareMessage(defaultRecipient));
      setAftercareEnabled(existing.email_enabled);
      if (existing.email_scheduled_date) {
        const remaining = Math.max(1, Math.ceil(moment(existing.email_scheduled_date).diff(moment(), 'days', true)));
        setAftercareDelayDays(remaining);
      } else {
        setAftercareDelayDays(5);
      }
    } else {
      setAftercareRecipientClientId(defaultRecipient);
      setAftercareMessage(getDefaultAftercareMessage(defaultRecipient));
      setAftercareDelayDays(5);
      setAftercareEnabled(true);
    }
    setAftercareDialogOpen(true);
  };

  const handleSaveAftercare = async () => {
    if (!record?.id) return;
    setAftercareSaving(true);
    try {
      const scheduledDate = moment().add(aftercareDelayDays, 'days').toISOString();
      const data = {
        funeral_id: Number(record.id),
        email_enabled: aftercareEnabled,
        email_recipient_client_id: aftercareRecipientClientId || null,
        email_message: aftercareMessage,
        email_scheduled_date: scheduledDate,
        created_by_user_id: identity?.id ? Number(identity.id) : null,
        created_by_user_name: identity?.fullName || 'System',
      };

      if (aftercare?.id) {
        await dataProvider.update('aftercare', {
          id: aftercare.id,
          data: {
            email_enabled: aftercareEnabled,
            email_recipient_client_id: aftercareRecipientClientId || null,
            email_message: aftercareMessage,
            email_scheduled_date: scheduledDate,
          },
          previousData: aftercare,
        });
        notify('Aftercare email updated', { type: 'success' });
      } else {
        await dataProvider.create('aftercare', { data });
        notify(`Aftercare email scheduled for ${aftercareDelayDays} days from now`, { type: 'success' });
      }
      setAftercareDialogOpen(false);
      void fetchAftercare();
    } catch (e: any) {
      notify(e.message || 'Failed to save aftercare email', { type: 'error' });
    } finally {
      setAftercareSaving(false);
    }
  };

  const handleDisableAftercare = async () => {
    if (!aftercare?.id) {
      setAftercareDialogOpen(false);
      return;
    }
    setAftercareSaving(true);
    try {
      await dataProvider.update('aftercare', {
        id: aftercare.id,
        data: { email_enabled: false },
        previousData: aftercare,
      });
      notify('Aftercare email disabled', { type: 'info' });
      setAftercareDialogOpen(false);
      void fetchAftercare();
    } catch (e: any) {
      notify(e.message || 'Failed to disable aftercare email', { type: 'error' });
    } finally {
      setAftercareSaving(false);
    }
  };

  const handleSendAftercareNow = async () => {
    if (!record?.id) return;
    setAftercareSaving(true);
    try {
      // Persist dialog fields first so the sent email uses the latest message/recipient.
      const data = {
        funeral_id: Number(record.id),
        email_enabled: true,
        email_recipient_client_id: aftercareRecipientClientId || null,
        email_message: aftercareMessage,
        email_scheduled_date: new Date().toISOString(),
        created_by_user_id: identity?.id ? Number(identity.id) : null,
        created_by_user_name: identity?.fullName || 'System',
      };

      if (aftercare?.id) {
        await dataProvider.update('aftercare', {
          id: aftercare.id,
          data: {
            email_enabled: true,
            email_recipient_client_id: aftercareRecipientClientId || null,
            email_message: aftercareMessage,
            email_scheduled_date: data.email_scheduled_date,
          },
          previousData: aftercare,
        });
      } else {
        await dataProvider.create('aftercare', { data });
      }

      const token = await getToken();
      const response = await fetch(`${baseUrl}/aftercare/${record.id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (response.ok && result.status === 'success') {
        notify('Aftercare email sent', { type: 'success' });
      } else {
        notify(result.message || 'Failed to send aftercare email', { type: 'error' });
      }
      setAftercareDialogOpen(false);
      void fetchAftercare();
      void refetchEvents?.();
    } catch (e: any) {
      notify(e.message || 'Failed to send aftercare email', { type: 'error' });
    } finally {
      setAftercareSaving(false);
    }
  };

  // ─── Attestation handlers ──────────────────────────────────────────────
  const openSendAttestationModal = () => {
    if (!record || !podDoc) return;
    setSendAttestationOpen(true);
  };

  // ─── Prep sheet handlers ──────────────────────────────────────────────
  const openPrepSheetModal = () => {
    const name = deceasedClient
      ? `${deceasedClient.first_name || ''} ${deceasedClient.last_name || ''}`.trim() || deceasedClient.name || ''
      : '';
    setPs({
      to: (crematoriumPartner as any)?.prep_sheet_emails || '',
      deceased_name: name,
      sex: deceasedClient?.sex || '',
      weight: deceasedClient?.weight ? String(deceasedClient.weight) : '',
      sp3: true,
      coroner: false,
      sp3_number: record?.sp3_number || '',
      causes_of_death: record?.causes_of_death_text || '',
      preparation: 'basic',
      pacemaker: 'no',
      shaving: 'no',
      shaving_detail: '',
      dentures: 'no',
      dentures_detail: '',
      clothing: 'sheet',
      cosmetics_no: true,
      hairdressing_photo: false,
      container: 'cardboard',
      hair: 'no',
      hair_count: '',
      notes: '',
    });
    setSendPrepSheetOpen(true);
  };

  const handleSendPrepSheet = async () => {
    if (!record) return;
    setSendingPrepSheet(true);
    try {
      const token = await getToken();
      const response = await fetch(`${baseUrl}/prep-sheet/${record.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(ps),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to send prep sheet');
      }

      // Create customer event for step tracking
      try {
        await dataProvider.create('customer_events', {
          data: {
            funeral_id: Number(record.id),
            timestamp: new Date().toISOString(),
            type: CustomerEventType.PREP_SHEET_SENT,
            contact_method: 'email',
            contact_detail: ps.to,
            message: 'Rideau Prep Sheet sent',
            user_id: identity?.id ? Number(identity.id) : undefined,
            user_name: identity?.fullName || 'System',
          },
        });
      } catch {
        /* event logging is best-effort */
      }

      notify('Prep sheet sent successfully', { type: 'success' });
      setSendPrepSheetOpen(false);
      void refetchEvents();
    } catch (e: any) {
      notify(e.message || 'Failed to send prep sheet', { type: 'error' });
    } finally {
      setSendingPrepSheet(false);
    }
  };

  // ─── Thank you card handlers ────────────────────────────────────────
  const openThankYouModal = () => {
    const card = (record as any)?.thank_you_card;
    if (card) {
      setTyRecipientName(card.recipientName || '');
      setTyPlaceName(card.placeName || '');
      setTyFloorSection(card.floorSection || '');
      setTyLetterText(card.letterText || '');

      // Parse address into separate fields
      if (card.street) {
        // New format: already split
        setTyStreet(card.street || '');
        setTyCity(card.city || '');
        setTyProvince(card.province || '');
        setTyPostalCode(card.postalCode || '');
      } else if (card.placeAddress) {
        // Legacy format: "street, city, province postalcode"
        const parts = card.placeAddress.split(',').map((p: string) => p.trim());
        setTyStreet(parts[0] || '');
        setTyCity(parts[1] || '');
        const provincePostal = parts[2] || '';
        const match = provincePostal.match(/^([A-Z]{2})\s+(.+)/i);
        setTyProvince(match ? match[1] : provincePostal);
        setTyPostalCode(match ? match[2] : '');
      } else {
        setTyStreet('');
        setTyCity('');
        setTyProvince('');
        setTyPostalCode('');
      }
    }
    setThankYouOpen(true);
  };

  const buildPlaceAddress = () =>
    [tyStreet, tyCity, [tyProvince, tyPostalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  const handleSaveThankYou = async () => {
    // Save edits back to funeral record without sending
    try {
      await dataProvider.update('funerals', {
        id: record.id,
        data: {
          thank_you_card: {
            ...((record as any)?.thank_you_card || {}),
            recipientName: tyRecipientName,
            placeName: tyPlaceName,
            placeAddress: buildPlaceAddress(),
            street: tyStreet,
            city: tyCity,
            province: tyProvince,
            postalCode: tyPostalCode,
            floorSection: tyFloorSection,
            letterText: tyLetterText,
          },
        },
        previousData: record,
      });
      notify('Thank you card saved', { type: 'success' });
      refresh();
      setThankYouOpen(false);
    } catch (error) {
      notify('Error saving', { type: 'error' });
    }
  };

  const handleSendThankYou = async () => {
    setSendingThankYou(true);
    try {
      const bodyText = tyLetterText;

      const response = await fetch(
        `${baseUrl}/funerals/${record.id}/send-thank-you-card`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getToken()}`,
          },
          body: JSON.stringify({
            recipientName: tyRecipientName,
            placeName: tyPlaceName,
            placeAddress: buildPlaceAddress(),
            floorSection: tyFloorSection,
            bodyText,
          }),
        },
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to send');
      }

      const result = await response.json();

      // Create customer event for timeline
      await dataProvider.create('customer_events', {
        data: {
          funeral_id: record.id,
          type: 'thank_you_card_sent' as CustomerEventType,
          timestamp: new Date().toISOString(),
          message: `Thank you card sent to ${tyRecipientName} at ${tyPlaceName}. Tracking: ${result.trackingId || 'pending'}`,
        },
      });

      notify('Thank you card sent!', { type: 'success' });
      setThankYouOpen(false);
      void refetchEvents();
      refresh();
    } catch (error: any) {
      notify(`Error: ${error.message}`, { type: 'error' });
    } finally {
      setSendingThankYou(false);
    }
  };

  if (!record) return null;

  // Does the current step have validation errors?
  const currentStep = currentStepIdx >= 0 ? steps[currentStepIdx] : null;
  const currentHasValidationErrors =
    currentStep && getMissingCount(currentStep) > 0;

  return (
    <div className="px-5 pt-4 pb-6">
      {steps.map((step, idx) => {
        const isCurrent = idx === currentStepIdx;
        const isLast = idx === steps.length - 1;
        const missingCount = getMissingCount(step);
        const Icon = ICON_MAP[step.icon];

        return (
          <div
            key={step.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '20px 1fr',
              gap: '0 10px',
            }}
          >
            {/* Icon + line column */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {/* Icon */}
              <div
                className={cn(
                  'mt-[1px] shrink-0',
                  step.completed ? step.color : 'text-muted-foreground/40',
                  isCurrent && !step.completed && step.color,
                )}
              >
                {Icon && <Icon style={{ width: 18, height: 18 }} />}
              </div>
              {/* Vertical line segment */}
              {!isLast && (
                <div
                  style={{
                    width: 1,
                    flex: 1,
                    minHeight: 8,
                    backgroundColor: '#e5e7eb',
                    marginTop: 4,
                  }}
                />
              )}
            </div>

            {/* Content column */}
            <div style={{ paddingBottom: isLast ? 0 : 16 }}>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={cn(
                    'text-[13px] leading-5',
                    step.completed && 'font-medium text-foreground',
                    isCurrent && !step.completed && 'font-medium text-foreground',
                    !step.completed && !isCurrent && 'text-muted-foreground',
                  )}
                >
                  {step.label}
                </span>

                {step.floating && !step.completed && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-normal text-amber-600 border-amber-300">
                    pending
                  </Badge>
                )}

                {missingCount > 0 && !step.completed && (
                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4 gap-0.5">
                    <AlertCircle className="w-2.5 h-2.5" />
                    {missingCount} required
                  </Badge>
                )}
              </div>

              {step.completed && step.completedAt && (
                <span className="text-[11px] text-muted-foreground leading-4 block mt-0.5">
                  {formatCompletedDate(step.completedAt)}
                </span>
              )}

              {step.id === 'partner_documents' && <PartnerDocsPanel funeral={record} />}

              {/* Untracked cases (dispatched before launch) have no step, but staff
                  can still see and send partner documents ad hoc. */}
              {step.id === 'transport_dispatched' &&
                step.completed &&
                !steps.some((s) => s.id === 'partner_documents') && (
                  <PartnerDocsPanel funeral={record} compact />
                )}

              {step.id === 'cremation_scheduled' && step.completed && record?.cremation_date && (
                <span className="text-[11px] text-muted-foreground leading-4 block mt-0.5">
                  Cremation: {moment(record.cremation_date).format('MMM D, YYYY')}
                </span>
              )}

              {isCurrent && currentHasValidationErrors && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-[12px] gap-1.5"
                  disabled={loadingMessage}
                  onClick={handleRequestMissingInfo}
                >
                  {loadingMessage ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <MessageSquareText className="w-3 h-3" />
                  )}
                  Request Missing Info
                </Button>
              )}

              {/* Send Benefits to Service Canada (postal) — opens the
                  existing PostGrid mailing modal. */}
              {step.id === 'send_benefits_to_service_canada' && !step.completed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-[12px] gap-1.5"
                  onClick={() => setCppMailingOpen(true)}
                >
                  <Mail className="w-3 h-3" />
                  Send by Mail
                </Button>
              )}
              {step.id === 'send_benefits_to_service_canada' && step.completed && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-6 text-[11px] gap-1 text-muted-foreground"
                  onClick={() => setCppMailingOpen(true)}
                >
                  <Mail className="w-3 h-3" />
                  Re-send
                </Button>
              )}

              {/* Send / Re-send paperwork-complete confirmation email (Quebec).
                  Surfaces once DEC + Benefits are both complete — the manual
                  entry point for cases where the auto-popup was dismissed. */}
              {step.id === 'paperwork_complete_notification' && !step.completed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-[12px] gap-1.5"
                  onClick={() => setPaperworkNotifyOpen(true)}
                >
                  <Mail className="w-3 h-3" />
                  Send Confirmation Email
                </Button>
              )}
              {step.id === 'paperwork_complete_notification' && step.completed && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-6 text-[11px] gap-1 text-muted-foreground"
                  onClick={() => setPaperworkNotifyOpen(true)}
                >
                  <Mail className="w-3 h-3" />
                  Re-send
                </Button>
              )}

              {/* Order Urn — opens the same dialog as the urn card's tracking
                  row, because ordering it and recording what came back are one
                  job. Rendered inside the step so there is exactly one. */}
              {step.id === 'order_urn' && record && (
                <>
                  <Button
                    variant={step.completed ? 'ghost' : 'outline'}
                    size="sm"
                    className={
                      step.completed
                        ? 'mt-1 h-6 text-[11px] gap-1 text-muted-foreground'
                        : 'mt-2 h-7 text-[12px] gap-1.5'
                    }
                    onClick={() => setUrnOrderOpen(true)}
                  >
                    <Package className="w-3 h-3" />
                    {step.completed ? 'Edit order' : 'Order urn'}
                  </Button>
                  <UrnOrderDialog
                    record={record as Funeral}
                    open={urnOrderOpen}
                    onOpenChange={setUrnOrderOpen}
                  />
                </>
              )}

              {/* Send / Re-send Attestation action button */}
              {step.id === 'send_attestation' && !step.completed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-[12px] gap-1.5"
                  onClick={() => {
                    if (podDoc) {
                      openSendAttestationModal();
                    } else {
                      setIssueDialogOpen(true);
                    }
                  }}
                >
                  <Mail className="w-3 h-3" />
                  {podDoc ? 'Send to Client' : 'Issue POD'}
                </Button>
              )}
              {step.id === 'send_attestation' && step.completed && podDoc && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-6 text-[11px] gap-1 text-muted-foreground"
                  onClick={() => openSendAttestationModal()}
                >
                  <Mail className="w-3 h-3" />
                  Re-send
                </Button>
              )}

              {/* Send / Re-send Prep Sheet action button */}
              {step.id === 'send_prep_sheet' && !step.completed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-[12px] gap-1.5"
                  onClick={openPrepSheetModal}
                >
                  <FileText className="w-3 h-3" />
                  Send Prep Sheet
                </Button>
              )}
              {step.id === 'send_prep_sheet' && step.completed && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-6 text-[11px] gap-1 text-muted-foreground"
                  onClick={openPrepSheetModal}
                >
                  <FileText className="w-3 h-3" />
                  Re-send
                </Button>
              )}

              {/* Send Thank You Card action button */}
              {step.id === 'send_thank_you_card' && !step.completed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-[12px] gap-1.5"
                  onClick={openThankYouModal}
                >
                  <Heart className="w-3 h-3" />
                  Review &amp; Send
                </Button>
              )}
              {step.id === 'send_thank_you_card' && step.completed && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-6 text-[11px] gap-1 text-muted-foreground"
                  onClick={openThankYouModal}
                >
                  <Heart className="w-3 h-3" />
                  View / Re-send
                </Button>
              )}

              {/* Aftercare action button */}
              {step.id === 'aftercare_email_sent' && (() => {
                const isSent = !!aftercare?.email_sent_at;
                const hasRow = !!aftercare;
                const isDisabled = hasRow && aftercare?.email_enabled === false;

                if (isSent) {
                  return (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-7 text-[12px] gap-1.5"
                      onClick={() => setAftercareViewOpen(true)}
                    >
                      <Heart className="w-3 h-3" />
                      View Email
                    </Button>
                  );
                }
                if (hasRow) {
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 h-7 text-[12px] gap-1.5"
                      onClick={() => openAftercareDialog(aftercare)}
                    >
                      <Heart className="w-3 h-3" />
                      {isDisabled ? 'Re-enable Email' : 'Update Email'}
                    </Button>
                  );
                }
                return (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 h-7 text-[12px] gap-1.5"
                    onClick={() => openAftercareDialog()}
                  >
                    <Heart className="w-3 h-3" />
                    Schedule Email
                  </Button>
                );
              })()}

              {/* Review Request action button */}
              {step.id === 'review_request_sent' && (() => {
                const isSent = !!reviewRequest?.sent_at;
                const hasRequest = !!reviewRequest;

                if (isSent) {
                  return (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-7 text-[12px] gap-1.5"
                      onClick={() => setReviewViewOpen(true)}
                    >
                      <Star className="w-3 h-3" />
                      View Request
                    </Button>
                  );
                }
                if (hasRequest) {
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 h-7 text-[12px] gap-1.5"
                      onClick={() => openReviewDialog(reviewRequest)}
                    >
                      <Star className="w-3 h-3" />
                      Update Request
                    </Button>
                  );
                }
                return (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 h-7 text-[12px] gap-1.5"
                    onClick={() => openReviewDialog()}
                  >
                    <Star className="w-3 h-3" />
                    Schedule Request
                  </Button>
                );
              })()}
            </div>
          </div>
        );
      })}

      {/* Issue Document Dialog (when no POD exists) */}
      <IssueDocumentDialog
        open={issueDialogOpen}
        onClose={() => setIssueDialogOpen(false)}
        funeral={record}
        onSuccess={() => {
          void fetchVerifiedDocs();
          setIssueDialogOpen(false);
        }}
        defaultDocType="POD"
      />

      {/* CPP Benefits postal mailing — wraps the existing PostGrid flow. */}
      <CppBenefitsMailingModal
        open={cppMailingOpen}
        onClose={() => setCppMailingOpen(false)}
        funeralUuid={(record as any)?.uuid || ''}
        funeralId={Number(record?.id) || 0}
        deceasedName={
          deceasedClient
            ? `${(deceasedClient as any).first_name || ''} ${(deceasedClient as any).last_name || ''}`.trim()
            : ''
        }
        deceasedProvince={(deceasedClient as any)?.province}
        deceasedPostalCode={(deceasedClient as any)?.postal_code}
        deceasedAddress={
          deceasedClient
            ? [
                (deceasedClient as any).civic_number || '',
                ' ',
                (deceasedClient as any).address || '',
                ', ',
                (deceasedClient as any).city || '',
                ', ',
                (deceasedClient as any).province || '',
                ', ',
                (deceasedClient as any).postal_code || '',
              ].join('').trim()
            : ''
        }
        buyerName={
          payingClient
            ? `${(payingClient as any).first_name || ''} ${(payingClient as any).last_name || ''}`.trim()
            : ''
        }
        buyerRelationship={(record as any)?.paying_client_relationship}
        onSuccess={() => {
          setCppMailingOpen(false);
          refresh();
        }}
      />

      {/* Paperwork-complete confirmation email (DEC + benefits numbers) */}
      <PaperworkCompleteNotificationDialog
        open={paperworkNotifyOpen}
        onClose={() => setPaperworkNotifyOpen(false)}
        funeral={record}
        onSent={() => { void refetchEvents(); }}
      />

      {/* Send Attestation Dialog */}
      <SendAttestationDialog
        open={sendAttestationOpen}
        onClose={() => setSendAttestationOpen(false)}
        verifiedDoc={podDoc}
        funeral={record}
        primaryClient={primaryClient}
        payingClient={payingClient}
        authorizedClient={authorizedClient}
        funeralDocs={funeralDocs}
        onSent={() => { void refetchEvents(); }}
      />

      {/* Send Prep Sheet Dialog */}
      <Dialog open={sendPrepSheetOpen} onClose={() => setSendPrepSheetOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Rideau Prep Sheet</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Send to (email)" value={ps.to} onChange={(e) => updatePs({ to: e.target.value })} fullWidth required placeholder="recipient@example.com" />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Information about the decedent</Typography>
            <TextField label="Full Name" value={ps.deceased_name} onChange={(e) => updatePs({ deceased_name: e.target.value })} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Gender" value={ps.sex} onChange={(e) => updatePs({ sex: e.target.value })} sx={{ flex: 1 }} />
              <TextField label="Weight" value={ps.weight} onChange={(e) => updatePs({ weight: e.target.value })} sx={{ flex: 1 }} />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel control={<Checkbox checked={ps.sp3} onChange={(e) => updatePs({ sp3: e.target.checked, coroner: !e.target.checked })} />} label="SP-3" />
              <FormControlLabel control={<Checkbox checked={ps.coroner} onChange={(e) => updatePs({ coroner: e.target.checked, sp3: !e.target.checked })} />} label="ADC (Coroner)" />
              <TextField label="Doc Number" value={ps.sp3_number} onChange={(e) => updatePs({ sp3_number: e.target.value })} size="small" sx={{ flex: 1 }} />
            </Stack>
            <TextField label="Causes of Death" value={ps.causes_of_death} onChange={(e) => updatePs({ causes_of_death: e.target.value })} fullWidth />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Preparation</Typography>
            <FormControl component="fieldset">
              <RadioGroup row value={ps.preparation} onChange={(e) => updatePs({ preparation: e.target.value as any })}>
                <FormControlLabel value="basic" control={<Radio />} label="Basic preparation" />
                <FormControlLabel value="embalming" control={<Radio />} label="Embalming" />
              </RadioGroup>
            </FormControl>
            <FormControl component="fieldset">
              <FormLabel sx={{ fontSize: '0.8rem' }}>Pacemaker</FormLabel>
              <RadioGroup row value={ps.pacemaker} onChange={(e) => updatePs({ pacemaker: e.target.value as any })}>
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl component="fieldset">
                <FormLabel sx={{ fontSize: '0.8rem' }}>Shaving</FormLabel>
                <RadioGroup row value={ps.shaving} onChange={(e) => updatePs({ shaving: e.target.value as any })}>
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                </RadioGroup>
              </FormControl>
              {ps.shaving === 'yes' && (
                <TextField label="Specify" value={ps.shaving_detail} onChange={(e) => updatePs({ shaving_detail: e.target.value })} size="small" sx={{ flex: 1 }} placeholder="beard, mustache, trim..." />
              )}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl component="fieldset">
                <FormLabel sx={{ fontSize: '0.8rem' }}>Dentures</FormLabel>
                <RadioGroup row value={ps.dentures} onChange={(e) => updatePs({ dentures: e.target.value as any })}>
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                </RadioGroup>
              </FormControl>
              {ps.dentures === 'yes' && (
                <TextField label="Specify" value={ps.dentures_detail} onChange={(e) => updatePs({ dentures_detail: e.target.value })} size="small" sx={{ flex: 1 }} placeholder="superior, inferior, partial..." />
              )}
            </Stack>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Clothing</Typography>
            <FormControl component="fieldset">
              <RadioGroup row value={ps.clothing} onChange={(e) => updatePs({ clothing: e.target.value as any })}>
                <FormControlLabel value="sheet" control={<Radio />} label="White sheet(s)" />
                <FormControlLabel value="pyjama" control={<Radio />} label="Pyjama Rideau" />
              </RadioGroup>
            </FormControl>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Cremation containers</Typography>
            <FormControl component="fieldset">
              <RadioGroup row value={ps.container} onChange={(e) => updatePs({ container: e.target.value as any })}>
                <FormControlLabel value="cardboard" control={<Radio />} label="Cardboard" />
                <FormControlLabel value="oversized" control={<Radio />} label="Oversized" />
              </RadioGroup>
            </FormControl>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl component="fieldset">
                <FormLabel sx={{ fontSize: '0.8rem' }}>Locks of hair</FormLabel>
                <RadioGroup row value={ps.hair} onChange={(e) => updatePs({ hair: e.target.value as any })}>
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                </RadioGroup>
              </FormControl>
              {ps.hair === 'yes' && (
                <TextField label="How many" value={ps.hair_count} onChange={(e) => updatePs({ hair_count: e.target.value })} size="small" sx={{ width: 100 }} />
              )}
            </Stack>

            <TextField label="Additional notes" value={ps.notes} onChange={(e) => updatePs({ notes: e.target.value })} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setSendPrepSheetOpen(false)} disabled={sendingPrepSheet}>Cancel</MuiButton>
          <MuiButton
            onClick={async () => {
              try {
                const token = await getToken();
                const res = await fetch(`${baseUrl}/prep-sheet/${record.id}/preview`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify(ps),
                });
                if (!res.ok) throw new Error('Failed to generate preview');
                const blob = await res.blob();
                window.open(URL.createObjectURL(blob), '_blank');
              } catch (e: any) {
                notify(e.message || 'Failed to preview', { type: 'error' });
              }
            }}
            variant="outlined"
            color="primary"
          >
            Preview
          </MuiButton>
          <MuiButton onClick={handleSendPrepSheet} variant="contained" color="primary" disabled={sendingPrepSheet || !ps.to.trim()}>
            {sendingPrepSheet ? <CircularProgress size={20} /> : 'Send'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Schedule/Update Review Request Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{reviewRequest?.id ? 'Update Review Request' : 'Schedule Review Request'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="review-recipient-label">Send to</InputLabel>
              <Select
                labelId="review-recipient-label"
                value={reviewRecipientClientId}
                label="Send to"
                onChange={(e) => {
                  const newId = e.target.value as number;
                  setReviewRecipientClientId(newId);
                  if (!reviewRequest?.id) {
                    setReviewMessage(getDefaultReviewMessage(newId));
                  }
                }}
              >
                {clientOptions.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name} ({client.type} Client)
                  </MenuItem>
                ))}
                {clientOptions.length === 0 && (
                  <MenuItem value="" disabled>
                    <em>No clients available</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>Send via</FormLabel>
              <RadioGroup
                row
                value={reviewSendMethod}
                onChange={(e) => setReviewSendMethod(e.target.value as ReviewRequestSendMethod)}
              >
                <FormControlLabel value="sms" control={<Radio />} label="SMS" />
                <FormControlLabel value="email" control={<Radio />} label="Email" />
                <FormControlLabel value="both" control={<Radio />} label="Both" />
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Message"
              value={reviewMessage}
              onChange={(e) => setReviewMessage(e.target.value)}
            />

            <TextField
              label="Send after (days)"
              type="number"
              value={reviewDelayDays}
              onChange={(e) => setReviewDelayDays(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1, max: 90 }}
              sx={{ width: 160 }}
            />

            <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon sx={{ color: '#1976d2', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Will be sent on: <strong>{moment().add(reviewDelayDays, 'days').format('MMMM D, YYYY')}</strong>
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setReviewDialogOpen(false)} disabled={reviewSaving}>Cancel</MuiButton>
          <MuiButton onClick={handleSendReviewNow} variant="outlined" color="primary" disabled={reviewSaving || !reviewRecipientClientId}>
            {reviewSaving ? <CircularProgress size={20} /> : 'Send Now'}
          </MuiButton>
          <MuiButton onClick={handleSaveReviewRequest} variant="contained" color="primary" disabled={reviewSaving || !reviewRecipientClientId}>
            {reviewSaving ? <CircularProgress size={20} /> : reviewRequest?.id ? 'Update' : 'Schedule'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* View Review Request Dialog (read-only) */}
      <Dialog open={reviewViewOpen} onClose={() => setReviewViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Request</DialogTitle>
        <DialogContent>
          {reviewRequest && (
            <Stack spacing={2} mt={1}>
              <Box>
                <Typography variant="caption" color="text.secondary">Recipient</Typography>
                <Typography>
                  {clientOptions.find((c) => c.id === reviewRequest.recipient_client_id)?.name || 'Unknown'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Send Method</Typography>
                <Typography>{getSendMethodLabel(reviewRequest.send_method)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Message</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>{reviewRequest.message}</Typography>
              </Box>
              {reviewRequest.sent_at && (
                <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="success.main" fontWeight={500}>
                    Sent on {new Date(reviewRequest.sent_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </Typography>
                </Box>
              )}
              {!reviewRequest.sent_at && reviewRequest.scheduled_date && (
                <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Scheduled for: <strong>{new Date(reviewRequest.scheduled_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setReviewViewOpen(false)}>Close</MuiButton>
        </DialogActions>
      </Dialog>

      {/* Schedule/Update Aftercare Email Dialog */}
      <Dialog open={aftercareDialogOpen} onClose={() => setAftercareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{aftercare?.id ? 'Update Aftercare Email' : 'Schedule Aftercare Email'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="aftercare-recipient-label">Send to</InputLabel>
              <Select
                labelId="aftercare-recipient-label"
                value={aftercareRecipientClientId}
                label="Send to"
                onChange={(e) => {
                  const newId = e.target.value as number;
                  setAftercareRecipientClientId(newId);
                  if (!aftercare?.id) {
                    setAftercareMessage(getDefaultAftercareMessage(newId));
                  }
                }}
              >
                {clientOptions.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name} ({client.type} Client)
                  </MenuItem>
                ))}
                {clientOptions.length === 0 && (
                  <MenuItem value="" disabled>
                    <em>No clients available</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Message"
              value={aftercareMessage}
              onChange={(e) => setAftercareMessage(e.target.value)}
            />

            <TextField
              label="Send after (days)"
              type="number"
              value={aftercareDelayDays}
              onChange={(e) => setAftercareDelayDays(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1, max: 90 }}
              sx={{ width: 160 }}
            />

            <Box sx={{ p: 1.5, bgcolor: '#fff1f2', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon sx={{ color: '#be123c', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Will be sent on: <strong>{moment().add(aftercareDelayDays, 'days').format('MMMM D, YYYY')}</strong>
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={aftercareEnabled}
                  onChange={(e) => setAftercareEnabled(e.target.checked)}
                />
              }
              label="Email enabled"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Box>
            {aftercare?.id && aftercare.email_enabled && (
              <MuiButton onClick={handleDisableAftercare} color="error" disabled={aftercareSaving}>
                Disable Email
              </MuiButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <MuiButton onClick={() => setAftercareDialogOpen(false)} disabled={aftercareSaving}>
              Cancel
            </MuiButton>
            <MuiButton
              onClick={handleSendAftercareNow}
              variant="outlined"
              color="primary"
              disabled={aftercareSaving || !aftercareRecipientClientId}
            >
              {aftercareSaving ? <CircularProgress size={20} /> : 'Send Now'}
            </MuiButton>
            <MuiButton
              onClick={handleSaveAftercare}
              variant="contained"
              color="primary"
              disabled={aftercareSaving || !aftercareRecipientClientId}
            >
              {aftercareSaving ? <CircularProgress size={20} /> : aftercare?.id ? 'Update' : 'Schedule'}
            </MuiButton>
          </Box>
        </DialogActions>
      </Dialog>

      {/* View Aftercare Email Dialog (read-only, after sent) */}
      <Dialog open={aftercareViewOpen} onClose={() => setAftercareViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aftercare Email</DialogTitle>
        <DialogContent>
          {aftercare && (
            <Stack spacing={2} mt={1}>
              <Box>
                <Typography variant="caption" color="text.secondary">Recipient</Typography>
                <Typography>
                  {clientOptions.find((c) => c.id === aftercare.email_recipient_client_id)?.name || 'Unknown'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Message</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>{aftercare.email_message || ''}</Typography>
              </Box>
              {aftercare.email_sent_at && (
                <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.main" fontWeight={500}>
                    Sent on {new Date(aftercare.email_sent_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setAftercareViewOpen(false)}>Close</MuiButton>
        </DialogActions>
      </Dialog>

      {/* ─── Thank You Card Modal ─────────────────────────────────────── */}
      <Dialog open={thankYouOpen} onClose={() => setThankYouOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Review & Send Thank You Card</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Recipient Name"
              value={tyRecipientName}
              onChange={(e) => setTyRecipientName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Place / Hospital"
              value={tyPlaceName}
              onChange={(e) => setTyPlaceName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Street Address"
              value={tyStreet}
              onChange={(e) => setTyStreet(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Floor / Section (optional)"
              value={tyFloorSection}
              onChange={(e) => setTyFloorSection(e.target.value)}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="City"
                value={tyCity}
                onChange={(e) => setTyCity(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Province"
                value={tyProvince}
                onChange={(e) => setTyProvince(e.target.value)}
                sx={{ width: '120px', flexShrink: 0 }}
                required
              />
              <TextField
                label="Postal Code"
                value={tyPostalCode}
                onChange={(e) => setTyPostalCode(e.target.value)}
                sx={{ width: '140px', flexShrink: 0 }}
                required
              />
            </Stack>
            <Box sx={{ bgcolor: '#f9fafb', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Card will read:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                Hi {tyRecipientName || '[Name]'},
              </Typography>
            </Box>
            <TextField
              label="Message"
              value={tyLetterText}
              onChange={(e) => {
                const newValue = e.target.value;
                if (countCardRows(newValue) <= MAX_CARD_ROWS) {
                  setTyLetterText(newValue);
                }
              }}
              fullWidth
              multiline
              minRows={5}
              required
              helperText={`${countCardRows(tyLetterText)}/${MAX_CARD_ROWS} lines`}
              FormHelperTextProps={{
                sx: { textAlign: 'right', color: countCardRows(tyLetterText) >= MAX_CARD_ROWS ? 'error.main' : 'text.secondary' },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setThankYouOpen(false)}>Cancel</MuiButton>
          <MuiButton
            onClick={handleSaveThankYou}
            variant="outlined"
            color="primary"
          >
            Save
          </MuiButton>
          <MuiButton
            onClick={handleSendThankYou}
            variant="contained"
            color="primary"
            disabled={sendingThankYou || !tyRecipientName.trim() || !tyStreet.trim() || !tyCity.trim() || !tyProvince.trim() || !tyPostalCode.trim() || !tyLetterText.trim()}
          >
            {sendingThankYou ? <CircularProgress size={20} /> : 'Send'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
